// w35 Terrakota — warm dried-floral wedding preview card.
//
// The full template is an earthy terracotta + olive + cream invitation
// (cotton, wheat, lavender, eucalyptus). The catalog card keeps that
// palette: a cream→parchment paper-fold frame, an inner cream card, and
// a focal medallion holding a pair of crossed wheat/eucalyptus sprigs.
// Names in the template font; eyebrow / footer use the standard
// small-caps treatment.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(135deg, #FAF4EB 0%, #EDE4D4 22%, #FAF4EB 48%, #EDE4D4 74%, #FAF4EB 100%)";

export function TerracottaPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const olive = "#7B8B5E";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: accent,
            marginBottom: "7%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* crossed wheat + eucalyptus sprig medallion */}
        <div style={{ width: "60%", aspectRatio: "1 / 1", position: "relative", marginBottom: "7%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px solid ${accent}66`,
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg viewBox="0 0 100 100" width="62%" height="62%" aria-hidden>
              {/* two stems crossing at the base */}
              <path d="M50 86 Q40 55 34 24" fill="none" stroke={olive} strokeWidth="2.2" strokeLinecap="round" />
              <path d="M50 86 Q60 55 66 24" fill="none" stroke={olive} strokeWidth="2.2" strokeLinecap="round" />
              {/* wheat grains on the left stem */}
              {[0.22, 0.4, 0.58, 0.76].map((p, i) => {
                const x = 34 + (50 - 34) * (1 - p) + (p - 0.5) * 2;
                const y = 24 + (86 - 24) * p;
                return (
                  <ellipse key={`l${i}`} cx={x - 5} cy={y} rx="4.4" ry="2"
                    fill={accent} transform={`rotate(-38 ${x - 5} ${y})`} />
                );
              })}
              {/* eucalyptus leaves on the right stem */}
              {[0.26, 0.46, 0.66, 0.84].map((p, i) => {
                const x = 66 - (66 - 50) * (1 - p) - (p - 0.5) * 2;
                const y = 24 + (86 - 24) * p;
                return (
                  <ellipse key={`r${i}`} cx={x + 5} cy={y} rx="4.8" ry="3.4"
                    fill={olive} opacity="0.85"
                    transform={`rotate(40 ${x + 5} ${y})`} />
                );
              })}
              {/* tie at the base */}
              <rect x="45" y="80" width="10" height="3.4" rx="1.4" fill={accent} />
            </svg>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 27, lineHeight: 1.1, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: accent,
            marginTop: "5%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
